import { GetProp, message, UploadFile, UploadProps } from 'antd'
import { useState } from 'react'

import { AppAdmin } from 'api/app-admin'
import { logError } from 'utils/logger'

type FileType = Parameters<GetProp<UploadProps, 'beforeUpload'>>[0]

interface UploadFileProps {
  mimeTypes?: string[]
  // upload single file
  onUpload?: (file: any) => Promise<void>
  onUploadSuccess?: (file: any) => void

  // upload multiple files
  onUploadMulti?: (files: any[]) => Promise<void>
  onUploadMultiSuccess?: (files: any[]) => void

  // before upload
  onBeforeUpload?: () => void
  onBeforeUploadMulti?: () => void
}

const useUploadFile = ({
  mimeTypes = ['image/jpeg', 'image/png'],
  onUpload,
  onUploadSuccess,

  onUploadMulti,
  onUploadMultiSuccess,

  onBeforeUpload,
  onBeforeUploadMulti,
}: UploadFileProps) => {
  const [fileList, setFileList] = useState<UploadFile[]>([])
  const [singleFile, setSingleFile] = useState<UploadFile[]>([])

  /**
   * func upload single file
   * @param info
   */
  const handleUploadSingle = async (info: any) => {
    const { file } = info
    if (onUpload) {
      await onUpload(file)
      return
    }
    const formData = new FormData()
    formData.append('file', file)
    try {
      const response = await AppAdmin.uploadSingleFile(formData)
      if (response.data.success) {
        message.success('File uploaded successfully')
        onUploadSuccess?.(response.data.data)
        //setThumbnail(response.data.data.filePath)
        //form.setFields([{ name: 'uploadSingle', errors: [] }])
      }
    } catch (error: any) {
      message.error(error?.message || 'File upload failed')
      logError('Upload error:', error)
    }
  }

  /**
   * Func upload multiple files
   */
  const handleUploadMulti = async () => {
    if (onUploadMulti) {
      await onUploadMulti(fileList)
      return
    }
    const formData = new FormData()
    fileList.forEach((file) => {
      formData.append('files', file as FileType)
    })
    try {
      const response = await AppAdmin.uploadMultipleFiles(formData)
      if (response.data.success) {
        message.success('File uploaded successfully')
        onUploadMultiSuccess?.(response.data.data)
      }
    } catch (error: any) {
      message.error(error?.message || 'Files upload failed')
      logError('Upload error:', error)
    } finally {
      setFileList([])
    }
  }

  /**
   * Func remove file
   * @param file
   */
  const handleRemoveMulti = (file: any) => {
    const index = fileList.indexOf(file)
    const newFileList = fileList.slice()
    newFileList.splice(index, 1)
    setFileList(newFileList)
  }

  const handleRemoveFile = () => {
    setSingleFile([])
  }

  /**
   * Func before upload single file
   * @param file
   * @returns
   */
  const beforeUploadSingle = async (file: FileType) => {
    const isAllowMimeTypes = mimeTypes.includes(file.type)
    if (!isAllowMimeTypes) {
      onBeforeUpload?.()
      return false
    }
    return true
  }

  /**
   * Func before upload multiple files
   * @param file
   * @returns
   */
  const beforeUploadMulti = async (file: FileType) => {
    const isAllowMimeTypes = mimeTypes.includes(file.type)
    if (!isAllowMimeTypes) {
      onBeforeUploadMulti?.()
    } else {
      setFileList([...fileList, file])
    }

    return false
  }

  return {
    singleFile,
    fileList,
    handleUploadSingle,
    handleUploadMulti,
    beforeUploadSingle,
    beforeUploadMulti,
    handleRemoveFile,
    handleRemoveMulti,
  }
}

export default useUploadFile
